import { useEffect, useMemo, useRef, useState } from 'react';
import { chartXAtPeriodIndex } from '../../lib/connectionDashboard';
import {
  buildSiteTrafficTrend,
  formatSiteTrendPeriodTooltip,
  getSiteTrendChartAxisLabels,
  siteAnalyticsTrendUnitLabel,
  type SiteTrafficTrendPoint,
} from '../../lib/siteAnalyticsTrend';
import type {
  SiteAnalyticsChartPeriodHours,
  SiteAnalyticsTrendPoint,
} from '../../lib/posthogSiteActivity';
import ChartHoverHighlight from '../Engine/ChartHoverHighlight';
import ChartTodayMarker from '../Engine/ChartTodayMarker';

type SiteTrafficTrendChartProps = {
  trend: SiteAnalyticsTrendPoint[];
  trendPeriodHours: SiteAnalyticsChartPeriodHours;
  className?: string;
};

const MIN_CHART_WIDTH = 320;
const MIN_CHART_HEIGHT = 200;

type SeriesCoords = {
  anonymous: { x: number; y: number }[];
  known: { x: number; y: number }[];
  anonymousPath: string;
  knownPath: string;
  anonymousAreaPath: string;
  maxY: number;
};

function buildSeries(
  points: SiteTrafficTrendPoint[],
  chartWidth: number,
  chartHeight: number,
): SeriesCoords {
  if (points.length === 0) {
    return {
      anonymous: [],
      known: [],
      anonymousPath: '',
      knownPath: '',
      anonymousAreaPath: '',
      maxY: 1,
    };
  }

  const maxV = Math.max(...points.flatMap((p) => [p.anonymousViews, p.knownViews]), 1);

  const anonymous = points.map((p, i) => {
    const x = points.length <= 1 ? chartWidth / 2 : (i / (points.length - 1)) * chartWidth;
    const y = chartHeight - (p.anonymousViews / maxV) * chartHeight;
    return { x, y };
  });

  const known = points.map((p, i) => {
    const x = points.length <= 1 ? chartWidth / 2 : (i / (points.length - 1)) * chartWidth;
    const y = chartHeight - (p.knownViews / maxV) * chartHeight;
    return { x, y };
  });

  const anonymousPath = anonymous
    .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(' ');
  const knownPath = known
    .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(' ');
  const anonymousAreaPath = `${anonymousPath} L ${anonymous[anonymous.length - 1].x.toFixed(1)} ${chartHeight.toFixed(1)} L ${anonymous[0].x.toFixed(1)} ${chartHeight.toFixed(1)} Z`;

  return { anonymous, known, anonymousPath, knownPath, anonymousAreaPath, maxY: maxV };
}

function findCurrentPointIndex(points: SiteTrafficTrendPoint[]): number | null {
  for (let i = points.length - 1; i >= 0; i--) {
    if (!points[i].isFuture) return i;
  }
  return null;
}

export default function SiteTrafficTrendChart({
  trend,
  trendPeriodHours,
  className = '',
}: SiteTrafficTrendChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState({ width: MIN_CHART_WIDTH, height: MIN_CHART_HEIGHT });
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateSize = () => {
      const { width, height } = element.getBoundingClientRect();
      setChartSize({
        width: Math.max(Math.round(width), MIN_CHART_WIDTH),
        height: Math.max(Math.round(height), MIN_CHART_HEIGHT),
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const { width: chartWidth, height: chartHeight } = chartSize;

  const points = useMemo(
    () => buildSiteTrafficTrend(trend, trendPeriodHours),
    [trend, trendPeriodHours],
  );

  const axisLabels = useMemo(
    () => getSiteTrendChartAxisLabels(points, trendPeriodHours),
    [points, trendPeriodHours],
  );

  const unitLabel = siteAnalyticsTrendUnitLabel(trendPeriodHours);

  const series = useMemo(
    () => buildSeries(points, chartWidth, chartHeight),
    [points, chartWidth, chartHeight],
  );

  const todayX = useMemo(() => {
    const idx = findCurrentPointIndex(points);
    if (idx == null) return null;
    return chartXAtPeriodIndex(idx, points.length, chartWidth);
  }, [points, chartWidth]);

  const hovered = hoverIndex != null ? points[hoverIndex] : null;
  const hoverX =
    hoverIndex != null && points.length > 0
      ? series.anonymous[hoverIndex]?.x ?? series.known[hoverIndex]?.x
      : null;
  const hoverXPercent = hoverX != null && chartWidth > 0 ? (hoverX / chartWidth) * 100 : null;

  const pickNearestIndex = (clientX: number, rect: DOMRect) => {
    if (points.length === 0 || chartWidth <= 0) return null;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const xInViewBox = ratio * chartWidth;
    let nearest = 0;
    let minDist = Infinity;
    series.anonymous.forEach((c, i) => {
      const dist = Math.abs(c.x - xInViewBox);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    return nearest;
  };

  if (points.length === 0) {
    return (
      <div
        className={`flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 min-h-[14rem] ${className}`}
      >
        No traffic data in this period
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative w-full min-w-0 min-h-[14rem] ${className}`}
    >
      <div className="absolute inset-0 z-0">
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          width={chartWidth}
          height={chartHeight}
          className="w-full h-full block cursor-crosshair"
          role="img"
          aria-label={`Site page views per ${unitLabel} over the last ${trendPeriodHours} hours`}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setHoverIndex(pickNearestIndex(e.clientX, rect));
          }}
          onMouseLeave={() => setHoverIndex(null)}
        >
          <defs>
            <linearGradient id="siteTrafficAnonymousFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(14 165 233)" stopOpacity="0.28" />
              <stop offset="100%" stopColor="rgb(14 165 233)" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {points.map((p, i) =>
            i > 0 ? (
              <line
                key={`site-traffic-grid-${p.periodStart}`}
                x1={series.anonymous[i]?.x ?? 0}
                y1={0}
                x2={series.anonymous[i]?.x ?? 0}
                y2={chartHeight}
                stroke="rgb(203 213 225)"
                strokeWidth={p.isHourMark ? 1.25 : 1}
                strokeOpacity={p.isHourMark ? 0.75 : 0.18}
                vectorEffect="non-scaling-stroke"
              />
            ) : null,
          )}
          <path d={series.anonymousAreaPath} fill="url(#siteTrafficAnonymousFill)" />
          <path
            d={series.anonymousPath}
            fill="none"
            stroke="rgb(14 165 233)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={series.knownPath}
            fill="none"
            stroke="rgb(16 185 129)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          <ChartTodayMarker x={todayX} height={chartHeight} />
          {hoverIndex != null && hoverX != null ? (
            <ChartHoverHighlight x={hoverX} height={chartHeight} />
          ) : null}
        </svg>
      </div>

      {hoverIndex != null && hoverXPercent != null && chartHeight > 0 ? (
        <>
          {[
            {
              y: series.anonymous[hoverIndex]?.y,
              className: 'bg-sky-500',
            },
            {
              y: series.known[hoverIndex]?.y,
              className: 'bg-emerald-500',
            },
          ].map((dot, i) =>
            dot.y != null ? (
              <div
                key={`site-traffic-hover-dot-${i}`}
                className={`pointer-events-none absolute z-20 h-2.5 w-2.5 rounded-full border-2 border-white shadow-sm ${dot.className}`}
                style={{
                  left: `${hoverXPercent}%`,
                  top: `${(dot.y / chartHeight) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              />
            ) : null,
          )}
        </>
      ) : null}

      <div className="absolute top-2 left-3 right-3 z-10 pointer-events-none">
        <div className="flex items-center gap-3 text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 drop-shadow-sm">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-0.5 bg-sky-500 rounded" />
            Anonymous
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-0.5 bg-emerald-500 rounded" />
            Known
          </span>
          <span>· {unitLabel}</span>
        </div>
      </div>

      {hovered && hoverXPercent != null ? (
        <div
          className="pointer-events-none absolute top-9 z-20 -translate-x-1/2 rounded-md border border-gray-200 dark:border-gray-600 bg-white/95 dark:bg-gray-800/95 px-2 py-1 shadow-sm whitespace-nowrap backdrop-blur-sm"
          style={{ left: `${hoverXPercent}%` }}
        >
          <div className="text-[10px] font-medium text-gray-900 dark:text-gray-100">
            {formatSiteTrendPeriodTooltip(hovered.periodStart, trendPeriodHours)}
          </div>
          <div className="text-[10px] tabular-nums text-gray-600 dark:text-gray-300">
            {hovered.isFuture ? (
              <span className="text-gray-500 dark:text-gray-400">No data yet</span>
            ) : (
              <>
                <span className="text-sky-600 dark:text-sky-400 font-semibold">
                  {hovered.anonymousViews}
                </span>{' '}
                anonymous
                <span className="mx-1 text-gray-300">·</span>
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                  {hovered.knownViews}
                </span>{' '}
                known
              </>
            )}
          </div>
        </div>
      ) : null}

      <div className="absolute bottom-1 left-3 right-3 z-10 flex justify-between text-[9px] text-gray-400/90 dark:text-gray-500 tabular-nums pointer-events-none">
        <span className="drop-shadow-sm">{axisLabels.start}</span>
        <span className="drop-shadow-sm">{axisLabels.end}</span>
      </div>

      <span className="sr-only">
        Peak traffic in period: {series.maxY.toLocaleString()} page views in one bucket.
      </span>
    </div>
  );
}
